import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { schedulingAPI } from '../../services/api';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  ChevronLeft, ChevronRight, Plus, X, Search, Calendar, Users,
  Lock, Check, DollarSign, User, Phone, Clock, Trash2, CheckCircle2
} from 'lucide-react';

/**
 * AgendaProPage — Google-Calendar-style scheduling grid.
 *
 * Modernized 2026-05-07:
 *  - Instagram-stories-style horizontal "professional carousel" at top — each
 *    avatar is small, circular, with active ring. Clicking switches the day's
 *    visible agenda to that single professional's column (filter mode).
 *  - QuickBookModal upgraded:
 *    • Toggle "Agendamento" / "Bloqueio" — bloqueio cria slot reservado sem
 *      cliente/serviço, indo direto para `is_block: true`.
 *    • Cliente search com autocomplete sobre `schedulingAPI.getClients`.
 *    • Quando editing existing appointment, mostra botão "Concluir & cobrar"
 *      que abre a forma de pagamento (alimenta financeiro automaticamente).
 *
 * Same source-of-truth as legacy "Agenda": both pull from the `appointments`
 * collection via `schedulingAPI`, so changes here surface there too.
 */

const SLOT_MIN = 30;

const STATUS_COLORS = {
  pendente:    { bg: 'bg-amber-100',   bd: 'border-amber-400',   tx: 'text-amber-900' },
  confirmado:  { bg: 'bg-emerald-100', bd: 'border-emerald-400', tx: 'text-emerald-900' },
  em_atendimento: { bg: 'bg-blue-100', bd: 'border-blue-400',    tx: 'text-blue-900' },
  concluido:   { bg: 'bg-slate-200',   bd: 'border-slate-400',   tx: 'text-slate-700' },
  cancelado:   { bg: 'bg-rose-100',    bd: 'border-rose-400',    tx: 'text-rose-900 line-through' },
};
const BLOCK_STYLE = { bg: 'bg-slate-300/70', bd: 'border-slate-500 border-dashed', tx: 'text-slate-700' };

const isoDate = (d) => {
  // Use LOCAL date components to avoid timezone shifts (Brazil UTC-3 issue).
  // Previously used d.toISOString().split('T')[0] which produced the wrong
  // day for local midnight values, hiding appointments from AgendaPro.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};
const addDays = (d, n) => { const c = new Date(d); c.setDate(c.getDate() + n); return c; };
const startOfWeek = (d) => { const c = new Date(d); c.setDate(c.getDate() - c.getDay()); return c; };
const initials = (name) => (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase();

const minutesFromHHMM = (hhmm) => {
  if (!hhmm) return 0;
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
};

// JS Date.getDay() → business_hours key
const DAY_KEYS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sab'];

// Build slot list from a business-hours range (start..end). Returns [] when
// inactive. Uses SLOT_MIN (30) increments. The end slot is exclusive (last
// bookable slot is end-SLOT_MIN, e.g. 18:00 → last slot is 17:30).
const slotsFromRange = (start, end) => {
  const out = [];
  const s = minutesFromHHMM(start);
  const e = minutesFromHHMM(end);
  for (let m = s; m < e; m += SLOT_MIN) {
    out.push(`${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`);
  }
  return out;
};

export default function AgendaProPage() {
  const [view, setView] = useState('day');
  const [date, setDate] = useState(new Date());
  const [professionals, setProfessionals] = useState([]);
  const [services, setServices] = useState([]);
  const [activeProfId, setActiveProfId] = useState('');   // '' = ALL pros (day view shows all columns); set = filter
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(null);
  const [businessHours, setBusinessHours] = useState(null); // {seg:{start,end,active}, ...}

  // Slots adapt to business hours. In Day view, use that day's range; in
  // Week view, use the widest active range so the grid has consistent rows
  // (closed-day cells are visually disabled later).
  const slots = useMemo(() => {
    if (!businessHours) return [];
    if (view === 'day') {
      const key = DAY_KEYS[date.getDay()];
      const h = businessHours[key] || {};
      if (!h.active) return [];
      return slotsFromRange(h.start || '08:00', h.end || '18:00');
    }
    // Week view: union of all active days
    let minS = 24 * 60, maxE = 0;
    Object.values(businessHours).forEach(h => {
      if (!h?.active) return;
      const s = minutesFromHHMM(h.start || '08:00');
      const e = minutesFromHHMM(h.end || '18:00');
      if (s < minS) minS = s;
      if (e > maxE) maxE = e;
    });
    if (maxE <= minS) return [];
    const fmt = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
    return slotsFromRange(fmt(minS), fmt(maxE));
  }, [businessHours, view, date]);

  useEffect(() => {
    Promise.all([
      schedulingAPI.getProfessionals().catch(() => ({ data: [] })),
      schedulingAPI.getServices().catch(() => ({ data: [] })),
      api.get('/scheduling/business-hours').catch(() => ({ data: null })),
    ]).then(([p, s, bh]) => {
      setProfessionals(p.data || []);
      setServices(s.data || []);
      setBusinessHours(bh.data || {
        seg: { start: '08:00', end: '18:00', active: true },
        ter: { start: '08:00', end: '18:00', active: true },
        qua: { start: '08:00', end: '18:00', active: true },
        qui: { start: '08:00', end: '18:00', active: true },
        sex: { start: '08:00', end: '18:00', active: true },
        sab: { start: '08:00', end: '13:00', active: true },
        dom: { start: '00:00', end: '00:00', active: false },
      });
    });
  }, []);

  const loadAppts = useCallback(async () => {
    setLoading(true);
    try {
      let start, end;
      if (view === 'day') { start = end = isoDate(date); }
      else {
        const w0 = startOfWeek(date);
        start = isoDate(w0);
        end = isoDate(addDays(w0, 6));
      }
      const r = await schedulingAPI.getAppointments({ start_date: start, end_date: end });
      setAppointments(r.data || []);
    } catch { toast.error('Erro ao carregar agendamentos'); }
    finally { setLoading(false); }
  }, [view, date]);
  useEffect(() => { loadAppts(); }, [loadAppts]);

  const goPrev = () => setDate(addDays(date, view === 'day' ? -1 : -7));
  const goNext = () => setDate(addDays(date, view === 'day' ? 1 : 7));
  const goToday = () => setDate(new Date());

  // Detect appointments whose professional_id doesn't match any current
  // professional (orphan/legacy) and bucket them under a synthetic "Sem
  // profissional" column so they don't silently disappear.
  const hasOrphans = useMemo(() => {
    if (view !== 'day') return false;
    const profIds = new Set(professionals.map(p => p.id));
    const today = isoDate(date);
    return appointments.some(a => a.date === today && (!a.professional_id || !profIds.has(a.professional_id)));
  }, [appointments, view, date, professionals]);

  const apptsByCol = useMemo(() => {
    const map = {};
    const profIds = new Set(professionals.map(p => p.id));
    for (const a of appointments) {
      if (view === 'day') {
        if (a.date !== isoDate(date)) continue;
        const k = (a.professional_id && profIds.has(a.professional_id)) ? a.professional_id : '__orphan__';
        if (activeProfId && k !== activeProfId) continue;
        (map[k] = map[k] || []).push(a);
      } else {
        if (activeProfId && a.professional_id !== activeProfId) continue;
        const k = a.date;
        (map[k] = map[k] || []).push(a);
      }
    }
    return map;
  }, [appointments, view, date, activeProfId, professionals]);

  const columns = useMemo(() => {
    if (view === 'day') {
      const list = activeProfId ? professionals.filter(p => p.id === activeProfId) : professionals;
      const cols = list.map(p => ({ id: p.id, label: p.name, photo: p.photo_url }));
      if (hasOrphans && !activeProfId) {
        cols.push({ id: '__orphan__', label: 'Sem profissional', photo: null });
      }
      return cols;
    }
    const w0 = startOfWeek(date);
    const wd = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'];
    return Array.from({ length: 7 }, (_, i) => {
      const d = addDays(w0, i);
      return {
        id: isoDate(d),
        dayKey: DAY_KEYS[d.getDay()],
        label: `${wd[i]} ${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`,
        isToday: isoDate(d) === isoDate(new Date()),
      };
    });
  }, [view, date, professionals, activeProfId, hasOrphans]);

  // Helper: check if a (column, slot) falls within that day's business hours.
  // In Day view we already filter slots to the day range upstream, so this
  // is only meaningful for Week view (to dim cells of closed/early-end days).
  const isSlotInBusinessHours = useCallback((col, slot) => {
    if (!businessHours || view === 'day') return true;
    const h = businessHours[col.dayKey];
    if (!h?.active) return false;
    const m = minutesFromHHMM(slot);
    return m >= minutesFromHHMM(h.start || '00:00') && m < minutesFromHHMM(h.end || '23:59');
  }, [businessHours, view]);

  const headerLabel = useMemo(() => {
    if (view === 'day') {
      return date.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' });
    }
    const w0 = startOfWeek(date);
    const w6 = addDays(w0, 6);
    return `${String(w0.getDate()).padStart(2, '0')}/${String(w0.getMonth() + 1).padStart(2, '0')} → ${String(w6.getDate()).padStart(2, '0')}/${String(w6.getMonth() + 1).padStart(2, '0')}`;
  }, [view, date]);

  const handleSlotClick = (col, slot) => {
    if (view === 'day') {
      setOpenModal({ date: isoDate(date), time: slot, professional_id: col.id });
    } else {
      setOpenModal({ date: col.id, time: slot, professional_id: activeProfId || (professionals[0]?.id || '') });
    }
  };

  const handleAptClick = (a) => setOpenModal({ apt: a });

  const onDropApt = async (apt, col, slot) => {
    if (!apt) return;
    const newDate = view === 'day' ? isoDate(date) : col.id;
    const newProfId = view === 'day' ? col.id : (apt.professional_id || activeProfId);
    if (apt.date === newDate && apt.time === slot && apt.professional_id === newProfId) return;
    try {
      await schedulingAPI.updateAppointment(apt.id, {
        date: newDate, time: slot, professional_id: newProfId,
      });
      toast.success('Agendamento movido');
      loadAppts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Falha ao mover');
    }
  };

  return (
    <div className="animate-fade-in" data-testid="agenda-pro-page">
      {/* Instagram-style professional carousel */}
      <div className="bg-white rounded-xl border border-slate-200 px-3 py-3 mb-3 overflow-x-auto" data-testid="agendapro-prof-carousel">
        <div className="flex items-center gap-3 min-w-max">
          {/* "Todos" / Equipe completa */}
          <button
            onClick={() => setActiveProfId('')}
            className="flex flex-col items-center gap-1 group"
            data-testid="agendapro-prof-all"
          >
            <div className={`w-14 h-14 rounded-full flex items-center justify-center transition ${activeProfId === '' ? 'ring-2 ring-offset-2 ring-blue-500' : 'ring-1 ring-slate-200'}`}>
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center text-white">
                <Users className="w-5 h-5" />
              </div>
            </div>
            <span className={`text-[10px] font-medium ${activeProfId === '' ? 'text-blue-600' : 'text-slate-500'}`}>Equipe</span>
          </button>

          {professionals.map(p => {
            const isActive = activeProfId === p.id;
            return (
              <button
                key={p.id}
                onClick={() => setActiveProfId(isActive ? '' : p.id)}
                className="flex flex-col items-center gap-1 group"
                data-testid={`agendapro-prof-${p.id}`}
              >
                <div className={`w-14 h-14 rounded-full flex items-center justify-center transition ${isActive ? 'ring-2 ring-offset-2 ring-blue-500' : 'ring-1 ring-slate-200 group-hover:ring-slate-400'}`}>
                  {p.photo_url ? (
                    <img src={p.photo_url} alt={p.name} className="w-12 h-12 rounded-full object-cover" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-400 to-cyan-500 flex items-center justify-center text-white text-xs font-bold">
                      {initials(p.name)}
                    </div>
                  )}
                </div>
                <span className={`text-[10px] font-medium max-w-[60px] truncate ${isActive ? 'text-blue-600' : 'text-slate-600'}`}>{p.name.split(' ')[0]}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <div className="flex bg-slate-100 rounded-lg p-0.5">
            <button onClick={() => setView('day')} className={`px-3 py-1.5 rounded-md text-xs font-semibold ${view === 'day' ? 'bg-white shadow text-slate-900' : 'text-slate-500'}`} data-testid="agendapro-view-day">
              <Calendar className="w-4 h-4 inline mr-1" /> Dia
            </button>
            <button onClick={() => setView('week')} className={`px-3 py-1.5 rounded-md text-xs font-semibold ${view === 'week' ? 'bg-white shadow text-slate-900' : 'text-slate-500'}`} data-testid="agendapro-view-week">
              <Users className="w-4 h-4 inline mr-1" /> Semana
            </button>
          </div>
          <button onClick={goPrev} className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50" data-testid="agendapro-prev"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={goToday} className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold hover:bg-slate-50" data-testid="agendapro-today">Hoje</button>
          <button onClick={goNext} className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50" data-testid="agendapro-next"><ChevronRight className="w-4 h-4" /></button>
          <span className="text-sm font-semibold text-slate-700 capitalize" data-testid="agendapro-header-label">{headerLabel}</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setOpenModal({ date: isoDate(date), time: '', professional_id: activeProfId || (professionals[0]?.id || ''), is_block: true })}
            className="text-xs px-3 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-50 flex items-center gap-1.5 text-slate-700"
            data-testid="agendapro-block-btn"
          >
            <Lock className="w-3.5 h-3.5" /> Bloquear
          </button>
          <button
            onClick={() => setOpenModal({ date: isoDate(date), time: '', professional_id: activeProfId || (professionals[0]?.id || '') })}
            className="btn-primary text-xs flex items-center gap-1.5"
            data-testid="agendapro-new-btn"
          >
            <Plus className="w-4 h-4" /> Novo
          </button>
        </div>
      </div>

      {/* Grid */}
      <div className="rounded-xl border border-slate-200 overflow-hidden bg-white">
        <div className="grid" style={{ gridTemplateColumns: `60px repeat(${columns.length || 1}, minmax(120px, 1fr))` }}>
          <div className="bg-slate-50 border-b border-slate-200 p-2"></div>
          {columns.map(col => (
            <div key={col.id} className={`bg-slate-50 border-b border-l border-slate-200 p-2 text-center ${col.isToday ? 'bg-primary/5' : ''}`}>
              {col.photo && <img src={col.photo} alt="" className="w-6 h-6 rounded-full inline mr-1" />}
              <span className="text-xs font-semibold text-slate-700">{col.label}</span>
            </div>
          ))}

          {slots.map((slot, sIdx) => (
            <React.Fragment key={slot}>
              <div className="border-b border-slate-100 p-1 text-[10px] text-slate-400 text-right pr-2 sticky left-0 bg-white" style={{ gridColumn: 1, gridRow: sIdx + 2 }}>
                {slot}
              </div>
              {columns.map((col, cIdx) => {
                const colKey = col.id;
                const colAppts = apptsByCol[colKey] || [];
                const slotMin = minutesFromHHMM(slot);
                const aptHere = colAppts.find(a => {
                  const am = minutesFromHHMM(a.time);
                  return am >= slotMin && am < slotMin + SLOT_MIN;
                });
                // Compute coverage on the fly to keep the closure simple.
                const isCoveredByEarlier = (() => {
                  for (let i = 1; i <= Math.min(sIdx, Math.ceil(720 / SLOT_MIN)); i++) {
                    const prev = (apptsByCol[colKey] || []).find(a => {
                      const am = minutesFromHHMM(a.time);
                      const prevSlotMin = minutesFromHHMM(slots[sIdx - i]);
                      if (am < prevSlotMin || am >= prevSlotMin + SLOT_MIN) return false;
                      const span = Math.max(1, Math.ceil((a.duration || 30) / SLOT_MIN));
                      return span > i;
                    });
                    if (prev) return true;
                  }
                  return false;
                })();
                if (isCoveredByEarlier) return null; // multi-slot apt above already covers this cell
                if (aptHere) {
                  const isBlock = !!aptHere.is_block;
                  const c = isBlock ? BLOCK_STYLE : (STATUS_COLORS[aptHere.status] || STATUS_COLORS.pendente);
                  const span = Math.max(1, Math.ceil((aptHere.duration || 30) / SLOT_MIN));
                  return (
                    <div
                      key={col.id + slot}
                      className={`border-b border-l border-slate-100 p-1 cursor-pointer hover:opacity-90 ${c.bg} ${c.bd} border-l-4 overflow-hidden`}
                      style={{ gridColumn: cIdx + 2, gridRow: `${sIdx + 2} / span ${span}` }}
                      draggable
                      onDragStart={(e) => { e.dataTransfer.setData('text/plain', aptHere.id); }}
                      onClick={() => handleAptClick(aptHere)}
                      data-testid={`agendapro-apt-${aptHere.id}`}
                    >
                      <div className={`text-[11px] font-bold leading-tight ${c.tx}`}>
                        {isBlock ? <Lock className="w-3 h-3 inline mr-1" /> : null}
                        {aptHere.time} {aptHere.customer_name}
                      </div>
                      <div className={`text-[10px] truncate ${c.tx}`}>{aptHere.service_name}</div>
                      <div className={`text-[10px] opacity-70 ${c.tx}`}>{aptHere.duration || 30}min</div>
                    </div>
                  );
                }
                return (
                  <div
                    key={col.id + slot}
                    className={`border-b border-l border-slate-100 transition ${isSlotInBusinessHours(col, slot) ? 'hover:bg-blue-50/50 cursor-pointer' : 'bg-slate-50/80 pointer-events-none'}`}
                    style={{ gridColumn: cIdx + 2, gridRow: sIdx + 2 }}
                    onClick={() => handleSlotClick(col, slot)}
                    onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                    onDrop={(e) => {
                      e.preventDefault();
                      const aptId = e.dataTransfer.getData('text/plain');
                      const draggedApt = appointments.find(x => x.id === aptId);
                      if (draggedApt) onDropApt(draggedApt, col, slot);
                    }}
                    data-testid={`agendapro-slot-${col.id}-${slot}`}
                  />
                );
              })}
            </React.Fragment>
          ))}
        </div>
        {view === 'day' && slots.length === 0 && (
          <div className="p-12 text-center" data-testid="agendapro-closed-day">
            <Lock className="w-10 h-10 mx-auto text-slate-300 mb-2" />
            <p className="text-sm font-semibold text-slate-600">Estabelecimento fechado neste dia</p>
            <p className="text-xs text-slate-400 mt-1">Para agendar fora do horario de funcionamento, use o botao <strong>Novo</strong> acima e informe a data/hora manualmente.</p>
          </div>
        )}
      </div>

      {loading && <div className="text-center text-xs text-slate-400 mt-3">Carregando…</div>}

      {openModal && (
        <QuickBookModal
          initial={openModal}
          professionals={professionals}
          services={services}
          onClose={() => setOpenModal(null)}
          onSaved={() => { setOpenModal(null); loadAppts(); }}
        />
      )}
    </div>
  );
}

// === Modal ==================================================================
const QuickBookModal = ({ initial, professionals, services, onClose, onSaved }) => {
  const editing = !!initial.apt;
  const apt = initial.apt || {};
  const [mode, setMode] = useState(initial.is_block || apt.is_block ? 'block' : 'apt'); // 'apt' | 'block'
  const [form, setForm] = useState({
    customer_name: apt.customer_name || '',
    customer_phone: apt.customer_phone || '',
    service_id: apt.service_id || (services[0]?.id || ''),
    extra_service_ids: (apt.extra_items || []).map(it => it.service_id).filter(Boolean),
    professional_id: apt.professional_id || initial.professional_id || (professionals[0]?.id || ''),
    date: apt.date || initial.date || isoDate(new Date()),
    time: apt.time || initial.time || '',
    notes: apt.notes || '',
    status: apt.status || 'pendente',
    block_duration: apt.duration || 30,
    block_reason: apt.block_reason || '',
  });
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  // Service search picker — modernized for shops with many services
  const [svcSearch, setSvcSearch] = useState('');
  const [svcPickerOpen, setSvcPickerOpen] = useState(false);

  // Client search autocomplete
  const [clientQuery, setClientQuery] = useState('');
  const [clientList, setClientList] = useState([]);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    if (mode !== 'apt' || !clientQuery || clientQuery.length < 2) { setClientList([]); return; }
    let active = true;
    const t = setTimeout(() => {
      schedulingAPI.getClients({ search: clientQuery })
        .then(r => { if (active) setClientList((r.data || []).slice(0, 8)); })
        .catch(() => { if (active) setClientList([]); });
    }, 200);
    return () => { active = false; clearTimeout(t); };
  }, [clientQuery, mode]);

  const pickClient = (c) => {
    setForm(f => ({ ...f, customer_name: c.name, customer_phone: c.phone || '' }));
    setClientQuery('');
    setShowClientDropdown(false);
  };

  // Conclude / payment state
  const [showConclude, setShowConclude] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentMethodId, setPaymentMethodId] = useState('');
  const [finalPrice, setFinalPrice] = useState(String((apt.price || 0).toFixed(2)));
  const [discountPct, setDiscountPct] = useState('');

  const openConclude = async () => {
    try {
      const r = await api.get('/scheduling/financial/payment-methods');
      setPaymentMethods((r.data || []).filter(m => m.enabled));
    } catch { setPaymentMethods([]); }
    setShowConclude(true);
  };
  const handleConclude = async () => {
    if (!paymentMethodId) return toast.error('Selecione a forma de pagamento');
    const selected = paymentMethods.find(m => m.id === paymentMethodId);
    try {
      const payload = { payment_method: selected?.type || 'outros', payment_method_id: paymentMethodId, is_courtesy: !!selected?.is_courtesy };
      const fp = parseFloat(finalPrice);
      if (!isNaN(fp) && fp !== (apt.price || 0)) payload.final_price = fp;
      const dp = parseFloat(discountPct);
      if (!isNaN(dp) && dp > 0) payload.discount_pct = dp;
      await schedulingAPI.concludeAppointment(apt.id, payload);
      toast.success('Atendimento concluido — financeiro atualizado');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao concluir'); }
  };

  const save = async () => {
    if (mode === 'block') {
      if (!form.professional_id) return toast.error('Selecione o profissional');
      if (!form.time) return toast.error('Informe o horário');
      setSaving(true);
      try {
        await schedulingAPI.createAppointment({
          customer_name: form.block_reason || 'Bloqueio',
          customer_phone: '',
          professional_id: form.professional_id,
          date: form.date, time: form.time,
          notes: form.block_reason || '',
          is_block: true, block_duration: parseInt(form.block_duration, 10) || 30,
          block_reason: form.block_reason || 'Indisponivel',
        });
        toast.success('Horario bloqueado');
        onSaved();
      } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao bloquear'); }
      finally { setSaving(false); }
      return;
    }
    if (!form.customer_name.trim()) return toast.error('Informe o nome do cliente');
    if (!form.service_id) return toast.error('Selecione o serviço');
    if (!form.professional_id) return toast.error('Selecione o profissional');
    if (!form.time) return toast.error('Informe o horário');
    setSaving(true);
    try {
      // Build payload: include extra_items for multi-service appointments.
      const extra_items = (form.extra_service_ids || [])
        .map(sid => services.find(s => s.id === sid))
        .filter(Boolean)
        .map(s => ({
          service_id: s.id,
          name: s.name,
          price: Number(s.price || 0),
          duration: Number(s.duration || s.duration_min || 30),
          type: 'service',
        }));
      const { extra_service_ids, ...rest } = form;
      const payload = { ...rest, extra_items };
      if (editing) {
        await schedulingAPI.updateAppointment(apt.id, payload);
        toast.success('Atualizado');
      } else {
        await schedulingAPI.createAppointment(payload);
        toast.success('Agendamento criado');
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const del = async () => {
    try {
      await api.delete(`/scheduling/appointments/${apt.id}`);
      toast.success(apt.is_block ? 'Bloqueio removido' : 'Excluido');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao excluir'); }
  };

  const isBlocking = mode === 'block' || apt.is_block;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-2 sm:p-4" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-xl shadow-2xl" onMouseDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()} data-testid="agendapro-modal">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h3 className="font-bold text-slate-900 flex items-center gap-2">
            {isBlocking ? <Lock className="w-4 h-4 text-slate-500" /> : <Calendar className="w-4 h-4 text-blue-500" />}
            {editing ? (apt.is_block ? 'Editar Bloqueio' : 'Editar Agendamento') : (mode === 'block' ? 'Bloquear Horário' : 'Novo Agendamento')}
          </h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>

        {/* Toggle Agendamento ↔ Bloqueio (only when creating new) */}
        {!editing && (
          <div className="px-4 pt-3">
            <div className="flex bg-slate-100 rounded-lg p-0.5" data-testid="agendapro-mode-toggle">
              <button
                onClick={() => setMode('apt')}
                className={`flex-1 px-3 py-1.5 rounded-md text-xs font-semibold transition ${mode === 'apt' ? 'bg-white shadow text-blue-700' : 'text-slate-500'}`}
                data-testid="agendapro-mode-apt"
              >
                <Calendar className="w-3.5 h-3.5 inline mr-1" /> Agendamento
              </button>
              <button
                onClick={() => setMode('block')}
                className={`flex-1 px-3 py-1.5 rounded-md text-xs font-semibold transition ${mode === 'block' ? 'bg-white shadow text-slate-700' : 'text-slate-500'}`}
                data-testid="agendapro-mode-block"
              >
                <Lock className="w-3.5 h-3.5 inline mr-1" /> Bloqueio
              </button>
            </div>
          </div>
        )}

        <div className="p-4 space-y-3 max-h-[65vh] overflow-y-auto">
          {/* Cliente: somente em mode='apt' */}
          {mode === 'apt' && !apt.is_block && (
            <>
              {!editing && (
                <div className="relative" ref={searchRef}>
                  <label className="text-[10px] font-bold uppercase text-slate-400 flex items-center gap-1"><Search className="w-3 h-3" /> Buscar cliente existente</label>
                  <input
                    value={clientQuery}
                    onChange={e => { setClientQuery(e.target.value); setShowClientDropdown(true); }}
                    onFocus={() => setShowClientDropdown(true)}
                    placeholder="Nome ou telefone..."
                    className="input-field text-sm"
                    data-testid="agendapro-client-search"
                  />
                  {showClientDropdown && clientList.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid="agendapro-client-dropdown">
                      {clientList.map(c => (
                        <button
                          key={c.id}
                          onClick={() => pickClient(c)}
                          className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-slate-100 last:border-0"
                          data-testid={`agendapro-client-pick-${c.id}`}
                        >
                          <div className="text-sm font-medium text-slate-800">{c.name}</div>
                          <div className="text-[11px] text-slate-500 flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] font-bold uppercase text-slate-400">Cliente</label>
                  <input value={form.customer_name} onChange={e => setForm({ ...form, customer_name: e.target.value })} className="input-field text-sm" data-testid="agendapro-customer-name" />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase text-slate-400">Telefone</label>
                  <input value={form.customer_phone} onChange={e => setForm({ ...form, customer_phone: e.target.value })} className="input-field text-sm" data-testid="agendapro-customer-phone" />
                </div>
              </div>
              {/* Modern service picker — chip + search */}
              {(() => {
                const mainSvc = services.find(s => s.id === form.service_id) || null;
                const extraSvcs = services.filter(s => form.extra_service_ids.includes(s.id) && s.id !== form.service_id);
                const totalDur = (mainSvc ? (mainSvc.duration || mainSvc.duration_min || 30) : 0)
                  + extraSvcs.reduce((sum, s) => sum + (s.duration || s.duration_min || 30), 0);
                const totalPrice = (mainSvc ? Number(mainSvc.price || 0) : 0)
                  + extraSvcs.reduce((sum, s) => sum + Number(s.price || 0), 0);
                const selectedIds = new Set([form.service_id, ...form.extra_service_ids].filter(Boolean));
                const results = svcSearch.trim()
                  ? services.filter(s => !selectedIds.has(s.id) && s.name.toLowerCase().includes(svcSearch.toLowerCase())).slice(0, 12)
                  : services.filter(s => !selectedIds.has(s.id)).slice(0, 12);
                const addSvc = (svc) => {
                  setForm(f => {
                    if (selectedIds.has(svc.id)) return f;
                    if (!f.service_id) return { ...f, service_id: svc.id };
                    return { ...f, extra_service_ids: [...f.extra_service_ids, svc.id] };
                  });
                  setSvcSearch('');
                  setSvcPickerOpen(false);
                };
                const removeSvc = (id) => {
                  setForm(f => {
                    if (f.service_id === id) {
                      const [first, ...rest] = f.extra_service_ids;
                      return { ...f, service_id: first || '', extra_service_ids: rest };
                    }
                    return { ...f, extra_service_ids: f.extra_service_ids.filter(x => x !== id) };
                  });
                };
                return (
                  <div>
                    <label className="text-[10px] font-bold uppercase text-slate-400">Servicos</label>
                    <div className="flex flex-wrap gap-1.5 mb-1.5 min-h-[28px]" data-testid="agendapro-selected-chips">
                      {[...(mainSvc ? [mainSvc] : []), ...extraSvcs].map(s => (
                        <span key={s.id} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary text-white text-xs font-semibold shadow-sm" data-testid={`agendapro-chip-${s.id}`}>
                          <span>{s.name}</span>
                          <span className="opacity-70">· {s.duration || s.duration_min || 30}min</span>
                          <button type="button" onClick={() => removeSvc(s.id)} className="ml-0.5 hover:bg-white/20 rounded-full w-4 h-4 flex items-center justify-center" data-testid={`agendapro-chip-remove-${s.id}`}>×</button>
                        </span>
                      ))}
                      {!mainSvc && <span className="text-xs text-slate-400 italic">Nenhum servico selecionado ainda</span>}
                    </div>
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                      <input
                        type="text"
                        value={svcSearch}
                        onChange={e => { setSvcSearch(e.target.value); setSvcPickerOpen(true); }}
                        onFocus={() => setSvcPickerOpen(true)}
                        onBlur={() => setTimeout(() => setSvcPickerOpen(false), 200)}
                        placeholder={mainSvc ? 'Adicionar outro servico…' : 'Pesquisar e adicionar servicos…'}
                        className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/20"
                        data-testid="agendapro-service-search"
                      />
                      {svcPickerOpen && results.length > 0 && (
                        <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-56 overflow-y-auto" data-testid="agendapro-service-picker">
                          {results.map(s => (
                            <button
                              key={s.id}
                              type="button"
                              onMouseDown={(e) => { e.preventDefault(); addSvc(s); }}
                              className="w-full text-left px-3 py-2 hover:bg-primary/5 flex items-center justify-between gap-2 border-b border-slate-50 last:border-0 transition"
                              data-testid={`agendapro-svc-pick-${s.id}`}
                            >
                              <span className="text-sm font-medium text-slate-800 truncate flex-1">{s.name}</span>
                              <span className="text-[11px] text-slate-500 whitespace-nowrap">{s.duration || s.duration_min || 30}min · R$ {Number(s.price || 0).toFixed(2)}</span>
                            </button>
                          ))}
                        </div>
                      )}
                      {svcPickerOpen && svcSearch.trim() && results.length === 0 && (
                        <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-xs text-slate-400 text-center">
                          Nenhum servico encontrado para "{svcSearch}"
                        </div>
                      )}
                    </div>
                    {(mainSvc || extraSvcs.length > 0) && (
                      <div className="mt-2 flex items-center justify-between text-[11px] text-slate-600 px-1" data-testid="agendapro-total-display">
                        <span>{(mainSvc ? 1 : 0) + extraSvcs.length} servico(s) selecionado(s)</span>
                        <span className="font-semibold">{totalDur} min · R$ {totalPrice.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                );
              })()}
            </>
          )}

          {/* Bloqueio fields */}
          {mode === 'block' && (
            <>
              <div>
                <label className="text-[10px] font-bold uppercase text-slate-400">Motivo do bloqueio</label>
                <input
                  value={form.block_reason}
                  onChange={e => setForm({ ...form, block_reason: e.target.value })}
                  placeholder="Ex.: Almoço, Reunião, Pessoal..."
                  className="input-field text-sm"
                  data-testid="agendapro-block-reason"
                />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase text-slate-400">Duração (min)</label>
                <select value={form.block_duration} onChange={e => setForm({ ...form, block_duration: e.target.value })} className="input-field text-sm" data-testid="agendapro-block-duration">
                  {[15, 30, 45, 60, 90, 120, 180, 240].map(m => <option key={m} value={m}>{m} min</option>)}
                </select>
              </div>
            </>
          )}

          {/* Comum a ambos */}
          <div>
            <label className="text-[10px] font-bold uppercase text-slate-400">Profissional</label>
            <select value={form.professional_id} onChange={e => setForm({ ...form, professional_id: e.target.value })} className="input-field text-sm" data-testid="agendapro-professional">
              <option value="">— Selecione —</option>
              {professionals.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-bold uppercase text-slate-400">Data</label>
              <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="input-field text-sm" data-testid="agendapro-date" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase text-slate-400">Hora</label>
              <input type="time" value={form.time} onChange={e => setForm({ ...form, time: e.target.value })} className="input-field text-sm" data-testid="agendapro-time" />
            </div>
          </div>

          {editing && !apt.is_block && (
            <div>
              <label className="text-[10px] font-bold uppercase text-slate-400">Status</label>
              <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className="input-field text-sm" data-testid="agendapro-status">
                <option value="pendente">Pendente</option>
                <option value="confirmado">Confirmado</option>
                <option value="em_atendimento">Em atendimento</option>
                <option value="concluido">Concluído</option>
                <option value="cancelado">Cancelado</option>
              </select>
            </div>
          )}

          {mode === 'apt' && (
            <div>
              <label className="text-[10px] font-bold uppercase text-slate-400">Observações</label>
              <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} className="input-field text-sm" data-testid="agendapro-notes" />
            </div>
          )}

          {/* Conclude inline panel */}
          {showConclude && editing && !apt.is_block && (
            <div className="border-t pt-3 mt-2 space-y-2 bg-emerald-50/50 -mx-4 px-4 pb-2 rounded-b" data-testid="agendapro-conclude-panel">
              <h4 className="text-xs font-bold text-emerald-700 flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" /> Concluir & cobrar</h4>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] font-bold uppercase text-slate-400">Valor final</label>
                  <input value={finalPrice} onChange={e => setFinalPrice(e.target.value)} className="input-field text-sm" data-testid="agendapro-final-price" />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase text-slate-400">Desconto (%)</label>
                  <input value={discountPct} onChange={e => setDiscountPct(e.target.value)} placeholder="0" className="input-field text-sm" data-testid="agendapro-discount-pct" />
                </div>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase text-slate-400">Forma de pagamento *</label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {paymentMethods.length === 0 && <p className="text-[11px] text-slate-500 col-span-2">Nenhuma forma de pagamento ativa. Cadastre em Financeiro → Formas de pagamento.</p>}
                  {paymentMethods.map(m => (
                    <button
                      key={m.id}
                      onClick={() => setPaymentMethodId(m.id)}
                      className={`text-left px-2 py-1.5 rounded-lg border text-xs transition ${paymentMethodId === m.id ? 'border-emerald-500 bg-emerald-100 text-emerald-900 font-semibold' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                      data-testid={`agendapro-pm-${m.id}`}
                    >
                      <div>{m.name}</div>
                      <div className="text-[10px] text-slate-500">{m.type}</div>
                    </button>
                  ))}
                </div>
              </div>
              <button
                onClick={handleConclude}
                disabled={!paymentMethodId}
                className="w-full bg-emerald-600 text-white py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-1.5 disabled:opacity-50"
                data-testid="agendapro-confirm-conclude"
              >
                <CheckCircle2 className="w-4 h-4" /> Concluir atendimento
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 p-4 border-t border-slate-200 bg-slate-50 rounded-b-2xl sm:rounded-b-xl">
          {editing ? (
            confirmDel ? (
              <button onClick={del} className="text-xs text-rose-600 font-semibold flex items-center gap-1" data-testid="agendapro-confirm-delete">
                <Trash2 className="w-3.5 h-3.5" /> Confirmar exclusão?
              </button>
            ) : (
              <button onClick={() => setConfirmDel(true)} className="text-xs text-rose-500 flex items-center gap-1" data-testid="agendapro-delete-btn">
                <Trash2 className="w-3.5 h-3.5" /> Excluir
              </button>
            )
          ) : <span />}
          <div className="flex gap-2 flex-wrap justify-end">
            {editing && !apt.is_block && !showConclude && apt.status !== 'concluido' && (
              <button onClick={openConclude} className="px-3 py-2 text-xs rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-1" data-testid="agendapro-open-conclude">
                <DollarSign className="w-3.5 h-3.5" /> Concluir
              </button>
            )}
            <button onClick={onClose} className="px-3 py-2 text-sm rounded border border-slate-300 hover:bg-white">Cancelar</button>
            {!showConclude && (
              <button onClick={save} disabled={saving} className="btn-primary text-sm" data-testid="agendapro-save-btn">{saving ? 'Salvando…' : (mode === 'block' ? 'Bloquear' : 'Salvar')}</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
